import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Link,
  Container,
  Fade,
  Slide,
  Stepper,
  Step,
  StepLabel,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Person,
  Business,
  Phone,
  HowToReg as RegisterIcon,
  ArrowBack,
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const Register = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    company: '',
    phone: '',
    role: 'user', // Standard-Rolle, muss freigeschaltet werden
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { register, isAuthenticated, error, clearError } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { if (isAuthenticated) navigate('/dashboard', { replace: true }); }, [isAuthenticated, navigate]);
  useEffect(() => () => clearError(), [clearError]);

  const steps = ['Persönliche Daten','Unternehmensdaten','Zugangsdaten'];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateStep = (step) => {
    const newErrors = {};
    switch (step) {
      case 0:
        if (!formData.name.trim()) newErrors.name = 'Name ist erforderlich';
        if (!formData.email.trim()) newErrors.email = 'E-Mail ist erforderlich';
        else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'E-Mail ist ungültig';
        break;
      case 1:
        if (!formData.company.trim()) newErrors.company = 'Unternehmensname ist erforderlich';
        break;
      case 2:
        if (!formData.password) newErrors.password = 'Passwort ist erforderlich';
        else if (formData.password.length < 8) newErrors.password = 'Mind. 8 Zeichen';
        if (!formData.confirmPassword) newErrors.confirmPassword = 'Bestätigung erforderlich';
        else if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwörter stimmen nicht überein';
        break;
      default: break;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => { if (validateStep(activeStep)) setActiveStep((s) => s + 1); };
  const handleBack = () => setActiveStep((s) => s - 1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(activeStep)) return;
    setIsSubmitting(true);
    
    // Nur die benötigten Felder für das Backend senden
    const { confirmPassword, ...registerData } = formData;
    const result = await register(registerData);
    
    setIsSubmitting(false);
    if (result.success) {
      // Direkt zum Dashboard weiterleiten ohne Hinweismeldung
      navigate('/dashboard', { replace: true });
    }
  };

  const handleTogglePasswordVisibility = () => setShowPassword(!showPassword);
  const handleToggleConfirmPasswordVisibility = () => setShowConfirmPassword(!showConfirmPassword);

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth label="Vollständiger Name" name="name" value={formData.name} onChange={handleChange} error={!!errors.name} helperText={errors.name} InputProps={{ startAdornment: (<InputAdornment position="start"><Person color="action" /></InputAdornment>) }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="E-Mail" name="email" type="email" value={formData.email} onChange={handleChange} error={!!errors.email} helperText={errors.email} InputProps={{ startAdornment: (<InputAdornment position="start"><Email color="action" /></InputAdornment>) }} />
            </Grid>
          </Grid>
        );
      case 1:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth label="Unternehmensname" name="company" value={formData.company} onChange={handleChange} error={!!errors.company} helperText={errors.company} InputProps={{ startAdornment: (<InputAdornment position="start"><Business color="action" /></InputAdornment>) }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Telefon (optional)" name="phone" value={formData.phone} onChange={handleChange} InputProps={{ startAdornment: (<InputAdornment position="start"><Phone color="action" /></InputAdornment>) }} />
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Hinweis:</strong> Ihr Account wird nach der Registrierung von einem Administrator freigeschaltet. 
                  Sie erhalten eine E-Mail-Benachrichtigung, sobald Ihr Account aktiviert wurde.
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        );
      case 2:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth label="Passwort" name="password" type={showPassword ? 'text' : 'password'} value={formData.password} onChange={handleChange} error={!!errors.password} helperText={errors.password} InputProps={{ startAdornment: (<InputAdornment position="start"><Lock color="action" /></InputAdornment>), endAdornment: (<InputAdornment position="end"><IconButton onClick={handleTogglePasswordVisibility} edge="end">{showPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment>) }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Passwort bestätigen" name="confirmPassword" type={showConfirmPassword ? 'text' : 'password'} value={formData.confirmPassword} onChange={handleChange} error={!!errors.confirmPassword} helperText={errors.confirmPassword} InputProps={{ startAdornment: (<InputAdornment position="start"><Lock color="action" /></InputAdornment>), endAdornment: (<InputAdornment position="end"><IconButton onClick={handleToggleConfirmPasswordVisibility} edge="end">{showConfirmPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment>) }} />
            </Grid>
          </Grid>
        );
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100vw',
        backgroundImage: 'url(/cosmos-7693800.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 0,
        m: 0,
        position: 'fixed',
        inset: 0,
        '&::before': { content: '""', position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 0 },
      }}
    >
      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2, px: { xs: 2, md: 0 } }}>
        <Fade in timeout={800}>
          <Card sx={{ borderRadius: 4, boxShadow: '0 25px 50px rgba(0,0,0,0.35)', background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255, 215, 0, 0.35)' }}>
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Slide in timeout={1000}>
                  <Box>
                    <Box sx={{ mb: 2 }}>
                      <img src="/logo.png" alt="KiChatbot Logo" style={{ height: '64px', width: 'auto' }} />
                    </Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>KiChatbot</Typography>
                    <Typography variant="body1" color="text.secondary">Admin Registrierung</Typography>
                  </Box>
                </Slide>
              </Box>

              {error && (<Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>)}

              <Box component="form" onSubmit={handleSubmit}>
                <Box sx={{ mb: 3, minHeight: { xs: 'auto', md: 300 } }}>{renderStepContent(activeStep)}</Box>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: 'space-between' }}>
                  <Button onClick={handleBack} disabled={activeStep === 0} startIcon={<ArrowBack />} sx={{ color: '#FFD700', fontWeight: 600 }}>Zurück</Button>
                  {activeStep === steps.length - 1 ? (
                    <Button type="submit" variant="contained" size="large" startIcon={<RegisterIcon />} disabled={isSubmitting} sx={{ textTransform: 'none', fontWeight: 600, px: 4 }}> {isSubmitting ? 'Registriere...' : 'Registrieren'} </Button>
                  ) : (
                    <Button onClick={handleNext} variant="contained" size="large" sx={{ textTransform: 'none', fontWeight: 600, px: 4 }}>Weiter</Button>
                  )}
                </Box>
              </Box>

              <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Bereits ein Konto?{' '}
                  <Link component={RouterLink} to="/login" sx={{ color: '#FFD700', textDecoration: 'none', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}>
                    Jetzt anmelden
                  </Link>
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Fade>
      </Container>
    </Box>
  );
};

export default Register;
